import { isValidElement, ReactNode, useEffect, useRef } from 'react';
import { TableColumn } from '@backstage/core-components';

// Column order/widths chosen by the user (drag to reorder, drag the handle to
// resize) are persisted in the browser's localStorage as ONE layout shared by
// every table wrapped in <ColumnLayoutPersistence>: reordering a column on any
// table moves it on every table that has a column with the same title.
// material-table exposes no callbacks through the Backstage table wrappers, so
// the layout is captured from the table header DOM and re-applied by the
// columns' rendered title text via applyColumnLayout.
const COLUMN_LAYOUT_STORAGE_KEY = 'savr.catalog-column-layout.v4';
// Keys/prefixes of earlier storage formats, removed on sight.
const LEGACY_STORAGE_KEYS = [
  'savr.catalog-table-column-layout',
  'savr.catalog-table-column-layout.v2',
];
const LEGACY_STORAGE_PREFIX = 'savr.catalog-column-layout.v3:';

// Fixed layout is required for column resizing; draggable enables reordering
// by dragging the column headers. Pass as tableOptions/options to the tables
// whose layout should be adjustable.
export const persistedTableOptions = {
  draggable: true,
  columnResizable: true,
  tableLayout: 'fixed' as const,
};

type ColumnLayout = { order: string[]; widths: Record<string, number> };

const readColumnLayout = (): ColumnLayout | undefined => {
  try {
    const raw = localStorage.getItem(COLUMN_LAYOUT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ColumnLayout) : undefined;
  } catch {
    return undefined;
  }
};

// Folds the header order observed on one table into the shared order: titles
// present on that table adopt the observed relative order (in the slots they
// already hold in the shared list), titles the table doesn't have keep their
// positions, and titles seen for the first time are inserted after the title
// that precedes them on the observed table.
const mergeOrder = (previous: string[], observed: string[]): string[] => {
  const merged = [...previous];
  const observedSet = new Set(observed);
  const slots: number[] = [];
  merged.forEach((title, i) => {
    if (observedSet.has(title)) {
      slots.push(i);
    }
  });
  const known = observed.filter(title => previous.includes(title));
  known.forEach((title, i) => {
    merged[slots[i]] = title;
  });
  observed
    .filter(title => !previous.includes(title))
    .forEach(title => {
      const precedingTitle = observed[observed.indexOf(title) - 1];
      const at =
        precedingTitle !== undefined ? merged.indexOf(precedingTitle) + 1 : 0;
      merged.splice(at, 0, title);
    });
  return merged;
};

// Plain-text header title for a column: our custom columns use string titles,
// while the stock catalog/techdocs columns render a title element whose
// translationKey's (English) text is the capitalized key
// ('name' -> 'Name', 'document' -> 'Document', ...).
const columnTitleText = <T extends object>(
  column: TableColumn<T>,
): string | undefined => {
  const { title } = column;
  if (typeof title === 'string') {
    return title;
  }
  if (isValidElement(title)) {
    const key = (title.props as { translationKey?: unknown }).translationKey;
    if (typeof key === 'string' && key.length > 0) {
      return key.charAt(0).toUpperCase() + key.slice(1);
    }
  }
  return undefined;
};

export const applyColumnLayout = <T extends object>(
  defaultColumns: TableColumn<T>[],
): TableColumn<T>[] => {
  const layout = readColumnLayout();
  if (!layout || !Array.isArray(layout.order)) {
    return defaultColumns;
  }
  // Columns whose title appears in the shared order are rearranged among the
  // slots they occupy; unrecognized columns stay where they are. Extra saved
  // titles (e.g. the untracked Actions column) are simply never matched.
  const entries = defaultColumns.map(column => ({
    column,
    title: columnTitleText(column),
  }));
  const result = [...entries];
  const slots: number[] = [];
  const known: typeof entries = [];
  entries.forEach((entry, i) => {
    if (entry.title !== undefined && layout.order.includes(entry.title)) {
      slots.push(i);
      known.push(entry);
    }
  });
  known
    .sort(
      (a, b) =>
        layout.order.indexOf(a.title as string) -
        layout.order.indexOf(b.title as string),
    )
    .forEach((entry, i) => {
      result[slots[i]] = entry;
    });
  // Widths must be restored in PIXELS for every visible column: material-
  // table's resizable-column code parseFloats width strings (so percentages
  // silently turn into px), and any column left unsized gets calc(100%/n)
  // and blows up the layout. Columns without a saved width get the average
  // of the saved ones. Left-over table width is distributed across columns
  // by the fixed table layout, so under-full sums still fill the table.
  const widthsByTitle = layout.widths ?? {};
  const savedWidths = result
    .filter(({ column }) => !column.hidden)
    .map(({ title }) =>
      title !== undefined ? widthsByTitle[title] : undefined,
    )
    .filter((width): width is number => typeof width === 'number');
  if (savedWidths.length === 0) {
    return result.map(({ column }) => column);
  }
  const average =
    savedWidths.reduce((sum, width) => sum + width, 0) / savedWidths.length;
  return result.map(({ title, column }) => {
    if (column.hidden) {
      return column;
    }
    const saved = title !== undefined ? widthsByTitle[title] : undefined;
    // Floor guards against ever rendering a collapsed column, whatever ends
    // up in storage.
    const width = Math.max(48, typeof saved === 'number' ? saved : average);
    return { ...column, width: `${Math.round(width)}px` };
  });
};

// Watches any table header rendered beneath it and saves the current column
// order and widths to localStorage whenever the user reorders or resizes
// columns. The header cells' text is the column title plus the "drag_handle"
// ligature text of the resize icon, which is stripped before matching.
export function ColumnLayoutPersistence({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) {
      return undefined;
    }
    let timer: number | undefined;

    try {
      LEGACY_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
      Object.keys(localStorage)
        .filter(key => key.startsWith(LEGACY_STORAGE_PREFIX))
        .forEach(key => localStorage.removeItem(key));
    } catch {
      // Ignore; purely cleanup.
    }

    const save = () => {
      const order: string[] = [];
      const widths: Record<string, number> = {};
      root.querySelectorAll('thead th').forEach(th => {
        const title = (th.textContent ?? '').replace(/drag_handle/g, '').trim();
        if (!title) {
          return;
        }
        order.push(title);
        widths[title] = th.getBoundingClientRect().width;
      });
      if (order.length === 0) {
        return;
      }
      // A column narrower than ~a resize handle is a transient state (table
      // initializing/re-rendering mid-load), never a layout the user chose -
      // persisting it would poison every table. Skip the whole snapshot.
      if (Object.values(widths).some(width => width < 24)) {
        return;
      }
      try {
        const previous = readColumnLayout();
        const next = JSON.stringify({
          order: mergeOrder(previous?.order ?? [], order),
          widths: { ...previous?.widths, ...widths },
        });
        if (localStorage.getItem(COLUMN_LAYOUT_STORAGE_KEY) !== next) {
          localStorage.setItem(COLUMN_LAYOUT_STORAGE_KEY, next);
        }
      } catch {
        // Ignore quota/access errors; persistence is best-effort.
      }
    };

    // table-layout: fixed ignores CSS min-width, so a resize drag could crush
    // a column to nothing and its content would overlap the neighbor. Clamp
    // the inline width material-table writes; re-triggered mutations settle
    // immediately since 48px passes the check.
    const clampWidths = () => {
      root.querySelectorAll<HTMLElement>('thead th').forEach(th => {
        const width = Number.parseFloat(th.style.width);
        if (!Number.isNaN(width) && width < 48) {
          th.style.width = '48px';
        }
      });
    };

    const observer = new MutationObserver(() => {
      clampWidths();
      window.clearTimeout(timer);
      timer = window.setTimeout(save, 250);
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style'],
    });
    // Escape hatch: double-click any table header row to reset the saved
    // column layout (order + widths) back to the defaults.
    const onDoubleClick = (event: Event) => {
      if ((event.target as HTMLElement | null)?.closest('thead')) {
        try {
          localStorage.removeItem(COLUMN_LAYOUT_STORAGE_KEY);
        } catch {
          // Ignore.
        }
        window.location.reload();
      }
    };
    root.addEventListener('dblclick', onDoubleClick);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      root.removeEventListener('dblclick', onDoubleClick);
    };
  }, []);

  return (
    <div ref={ref} style={{ display: 'contents' }}>
      {children}
    </div>
  );
}
